import { alpha, createTheme } from '@mui/material/styles';

export function buildTheme({ mode = 'dark' } = {}) {
    const isDark = mode === 'dark';

    // Light-mode colors tuned to the attached Admin dashboard look.
    const baseBg = isDark ? '#0b1220' : '#f1f5f9';
    const appBackground = isDark
        ? [
            'radial-gradient(circle at -10% -14%, rgba(96, 165, 250, 0.14), transparent 34%)',
            'radial-gradient(circle at 108% 2%, rgba(37, 99, 235, 0.11), transparent 34%)',
            'radial-gradient(circle at 50% 112%, rgba(15, 23, 42, 0.72), transparent 40%)',
            'linear-gradient(180deg, #08101d 0%, #0b1220 44%, #0e1727 100%)'
        ].join(', ')
        : [
            'radial-gradient(circle at -8% -12%, rgba(36, 50, 66, 0.18), transparent 38%)',
            'radial-gradient(circle at 108% 2%, rgba(29, 78, 216, 0.14), transparent 36%)',
            'radial-gradient(circle at 50% 112%, rgba(14, 165, 233, 0.12), transparent 42%)',
            'linear-gradient(180deg, #e7eef6 0%, #dbe5ee 46%, #e4ebf2 100%)'
        ].join(', ');
    const panelBg = isDark ? '#0f172a' : '#ffffff';
    const panelGlass = isDark ? alpha(panelBg, 0.72) : alpha(panelBg, 0.76);
    const panelGlassStrong = isDark ? alpha(panelBg, 0.84) : alpha(panelBg, 0.88);
    const drawerBg = isDark ? '#0b1220' : '#243242';
    const drawerGradient = isDark
        ? 'linear-gradient(135deg, #0b1220 0%, #0f172a 100%)'
        : 'linear-gradient(135deg, #243242 0%, #1d4ed8 100%)';
    const drawerText = isDark ? '#e5e7eb' : '#e6eef8';
    const drawerMuted = isDark ? '#94a3b8' : alpha('#e6eef8', 0.7);
    const divider = isDark ? alpha('#e2e8f0', 0.08) : alpha('#0f172a', 0.10);

    return createTheme({
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary: { main: isDark ? '#60a5fa' : '#2f80ed' },
            secondary: { main: isDark ? '#a78bfa' : '#7c3aed' },
            background: {
                default: baseBg,
                paper: panelBg
            },
            divider,
            text: {
                primary: isDark ? '#e5e7eb' : '#0f172a',
                secondary: isDark ? '#94a3b8' : '#64748b'
            }
        },
        shape: {
            borderRadius: 7
        },
        typography: {
            fontFamily: [
                'Inter',
                'system-ui',
                '-apple-system',
                'Segoe UI',
                'Roboto',
                'Arial'
            ].join(','),

            // Slightly smaller, more compact typography on phones.
            h4: {
                fontWeight: 900,
                fontSize: '2.00rem',
                lineHeight: 1.1,
                '@media (max-width:600px)': {
                    fontSize: '1.55rem'
                }
            },
            h5: {
                fontWeight: 900,
                fontSize: '1.45rem',
                lineHeight: 1.15,
                '@media (max-width:600px)': {
                    fontSize: '1.25rem'
                }
            },
            subtitle1: {
                fontSize: '1.00rem',
                '@media (max-width:600px)': {
                    fontSize: '0.95rem'
                }
            },
            body1: {
                fontSize: '0.95rem',
                '@media (max-width:600px)': {
                    fontSize: '0.92rem'
                }
            },
            body2: {
                fontSize: '0.90rem',
                '@media (max-width:600px)': {
                    fontSize: '0.88rem'
                }
            },
            caption: {
                fontSize: '0.80rem',
                '@media (max-width:600px)': {
                    fontSize: '0.78rem'
                }
            }
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    html: {
                        // Prevent iOS Safari text auto-zooming and keep type consistent.
                        WebkitTextSizeAdjust: '100%'
                    },
                    body: {
                        backgroundColor: baseBg,
                        backgroundImage: appBackground,
                        backgroundAttachment: 'fixed',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover'
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: panelGlass,
                        border: `1px solid ${isDark ? alpha('#ffffff', 0.06) : alpha('#ffffff', 0.58)}`,
                        boxShadow: isDark
                            ? '0 22px 48px rgba(2, 6, 23, 0.28)'
                            : '0 20px 44px rgba(15, 23, 42, 0.08)',
                        backdropFilter: 'blur(18px)',
                        WebkitBackdropFilter: 'blur(18px)'
                    }
                }
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: panelGlassStrong,
                        color: isDark ? '#e5e7eb' : '#0f172a',
                        boxShadow: isDark
                            ? '0 18px 42px rgba(2, 6, 23, 0.34)'
                            : '0 18px 40px rgba(15, 23, 42, 0.06)',
                        backdropFilter: 'blur(18px)',
                        WebkitBackdropFilter: 'blur(18px)',
                        borderBottom: `1px solid ${divider}`
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 999,
                        fontWeight: 700,
                        paddingLeft: 16,
                        paddingRight: 16,
                        '@media (max-width:600px)': {
                            paddingLeft: 14,
                            paddingRight: 14
                        }
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 999
                    }
                }
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundImage: drawerGradient,
                        backgroundColor: drawerBg,
                        color: drawerText
                    }
                }
            },
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        color: isDark ? '#94a3b8' : drawerMuted
                    }
                }
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 12
                    }
                }
            }
        }
    });
}
